import { collection, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { useAuth, useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { recipientCollectionPath } from '../schemas/paths';
import type { Recipient } from '../schemas';

export interface UseRecipientsResult {
  recipients: Recipient[];
  hydrated: boolean;
  error: Error | null;
  /** THE-1147 QA re-review: true only once a snapshot has been CONFIRMED by
   *  the server (`metadata.fromCache === false`), as opposed to `hydrated`,
   *  which flips on the very first event — including a from-cache snapshot
   *  that can be empty (e.g. a recipient created on another device, or this
   *  one's first visit after clearing storage) and including a listener
   *  error, neither of which means "the server has confirmed there's
   *  nothing on file." Most consumers want `hydrated` (it's the one that
   *  unblocks a cache-first instant paint); a consumer that is about to make
   *  a one-time, hard-to-undo decision from `recipients` being empty (the
   *  attach-quiz "load first" gate) should gate on this instead. */
  serverConfirmed: boolean;
}

// Subscribes to `theaWebUser/{uid}/recipient` and returns the recipient
// docs as they arrive. On auth uid swap (anon → permanent via the merge
// flow) the listener resets and re-binds so stale anon ids don't bleed
// into the permanent uid's view. Same shape pattern as `useGiftActivities`.
export function useRecipients(): UseRecipientsResult {
  const auth = useAuth();
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [serverConfirmed, setServerConfirmed] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      const nextUid = user?.uid ?? null;
      setUid((prev) => {
        if (prev === nextUid) return prev;
        setRecipients([]);
        setHydrated(false);
        setServerConfirmed(false);
        setError(null);
        return nextUid;
      });
    });
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    setError(null);

    // Defensive: in test contexts where `firebaseConfig` is mocked without
    // `ensureAuth` (most legacy unit tests for surfaces that incidentally
    // mount HeaderAccountMenu), the FirebaseContext fallback returns
    // undefined. Treat as "no uid available" — don't subscribe, leave
    // recipients empty, mark hydrated so consumers don't render a
    // spinner forever. Production always wires `ensureAuth` via
    // `FirebaseProvider`, so this branch never fires there.
    if (typeof ensureAuth !== 'function') {
      setHydrated(true);
      setServerConfirmed(true);
      return;
    }

    // Bind the identity this effect run was SCHEDULED for, not whatever
    // `ensureAuth()` happens to resolve. During an anon→permanent transition
    // `ensureAuth()` can hand back the OLD uid a beat late (a stale in-flight
    // anon sign-in, or a separate hook instance); `uid` from the authoritative
    // `onAuthStateChanged` is this run's real identity (a newer one would have
    // cancelled it). `resolvedUid` is used ONLY to bootstrap the null case
    // (cold load / fresh anon mint), so a settled account is never stranded
    // without a subscription. (Earlier this SKIPPED on a mismatch, which
    // suppressed the wrong binding but could strand a settled account with no
    // resubscription; binding the known identity converges by construction.)
    const expectedUid = uid;

    ensureAuth()
      .then((resolvedUid) => {
        if (cancelled) return;
        // Known identity → bind it directly; `resolvedUid` bootstraps null.
        const targetUid = expectedUid ?? resolvedUid;
        const ref = collection(db, ...recipientCollectionPath(targetUid));
        unsubscribe = onSnapshot(
          ref,
          // `includeMetadataChanges`: without it, Firestore only fires a
          // SECOND event on a cache→server transition if the data itself
          // changed — a cache snapshot that already matches the server
          // would otherwise be the only event we ever see, and
          // `serverConfirmed` below would never flip even though the
          // server has, in fact, confirmed it.
          { includeMetadataChanges: true },
          (snap) => {
            if (cancelled) return;
            const next: Recipient[] = [];
            snap.forEach((d) => {
              const data = d.data() as Partial<Recipient>;
              // Skip docs with archivedAt set so the UI doesn't render
              // recipients the user explicitly removed.
              if (data.archivedAt) return;
              next.push({ recipientId: d.id, ...data } as Recipient);
            });
            // Sort newest-first by createdAt so the most recently added
            // person sits at the top of the grid.
            next.sort((a, b) => {
              const aMs = a.createdAt?.toMillis?.() ?? 0;
              const bMs = b.createdAt?.toMillis?.() ?? 0;
              return bMs - aMs;
            });
            setRecipients(next);
            setHydrated(true);
            // THE-1147 QA re-review: a from-cache snapshot (metadata.fromCache)
            // can be empty or stale — e.g. a recipient created on another
            // device, or this one's first visit after clearing storage. Only a
            // server-confirmed snapshot proves there is genuinely nothing on
            // file, so only that flips `serverConfirmed` — see the interface
            // docstring. `recipients`/`hydrated` still update from the cache
            // snapshot so every OTHER consumer keeps its cache-first instant
            // paint unchanged.
            if (!snap.metadata?.fromCache) {
              setServerConfirmed(true);
            }
          },
          (err) => {
            if (cancelled) return;
            setError(err);
            setHydrated(true);
            // Deliberately NOT setting `serverConfirmed` here (THE-1147 QA
            // re-review): an error means we'll never get a real answer from
            // THIS listener, but it still is not the server confirming
            // "nothing on file" — a consumer gating a hard-to-undo decision
            // on `serverConfirmed` should fall through to its own bounded
            // timeout instead of treating the error as a confident answer.
          },
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setHydrated(true);
      });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [uid, db, ensureAuth]);

  return { recipients, hydrated, serverConfirmed, error };
}
